import React, { createContext, useState, useEffect, useContext } from 'react';
import { loginUser } from './firebase';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();
const APP_VERSION = '1.0.0'; // Versión actual de la aplicación

// Constantes para identificación de usuarios
const EXTERNAL_USER_ID = 'usuario_externo';
const SESSION_DURATION = 12 * 60 * 60 * 1000; // 12 horas en milisegundos

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    const savedVersion = localStorage.getItem('app_version');
    
    if (savedUser) {
      const parsedUser = JSON.parse(savedUser);
      
      // Verificar si la sesión ha expirado
      if (parsedUser.loginTimestamp && Date.now() - parsedUser.loginTimestamp > SESSION_DURATION) {
        //console.log('Sesión expirada');
        localStorage.clear();
        setUser(null);
        return;
      }

      // Verificar versión de la aplicación
      if (!savedVersion || savedVersion !== APP_VERSION) {
        //console.log('Versión de la aplicación actualizada');
        localStorage.clear();
        setUser(null);
        return;
      }

      setUser(parsedUser);
    }
  }, []);

  const login = async (id, password, defaultServer = null) => {
    const result = await loginUser(id.toLowerCase(), password);
    //console.log('🔑 Resultado de login:', result);
    
    if (result.success) {
      const isExternal = id.toLowerCase() === EXTERNAL_USER_ID;
      
      let userData = {
        ...result.userData,
        loginTimestamp: Date.now(),
        isExternalUser: isExternal,
        sessionExpires: Date.now() + SESSION_DURATION
      };

      if (isExternal) {
        userData = {
          ...userData,
          id: EXTERNAL_USER_ID,
          level: 'external',
          name: 'Usuario Externo',
          // Usuario externo no necesita servidor asignado ni servers_access
          server: null,
          servers_access: null
        };
        //console.log('👤 Usuario externo configurado con acceso total');
      } else {
        // Para usuarios normales, verificar y procesar el servidor asignado
        if (!userData.server) {
          //console.error('❌ Error: Usuario sin servidor asignado');
          // Si no tiene servidor asignado, asignar amgroupradio como predeterminado
          userData.server = 'https://amgroupradio.web.app/amgroupradio';
          //console.log('ℹ️ Asignando servidor predeterminado: amgroupradio');
        }
      }

      // Guardar datos del usuario
      //console.log('💾 Guardando datos de usuario:', userData);
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('app_version', APP_VERSION);

      try {
        // Para usuarios externos, redirigir a la ruta raíz
        if (isExternal) {
          return { 
            success: true, 
            redirectPath: '/',
            userData: userData
          };
        }

        // Para usuarios normales, redirigir a su servidor asignado
        const url = new URL(userData.server);
        const pathName = url.pathname.substring(url.pathname.lastIndexOf('/') + 1);
        //console.log('🔄 Redirigiendo a:', {
        //  servidor: userData.server,
        //  ruta: pathName,
        //  nivel: userData.level
        //});

        return { 
          success: true, 
          redirectPath: `/${pathName}`,
          userData: userData
        };
      } catch (error) {
        //console.error('❌ Error al procesar URL para redirección:', error);
        return { success: false, message: 'Error al procesar URL del servidor' };
      }
    } else {
      //console.error('❌ Error en login:', result.message);
      return { success: false, message: result.message };
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.clear();
  };

  const authContextValue = {
    user,
    login,
    logout,
    appVersion: APP_VERSION,
    isSessionValid: () => {
      if (!user || !user.loginTimestamp) return false;
      return Date.now() - user.loginTimestamp <= SESSION_DURATION;
    }
  };

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  );
};


